/**
 * Whisper model catalog + download progress helpers.
 *
 * Models are fetched by @huggingface/transformers and cached in the browser
 * Cache Storage / IndexedDB (env.useBrowserCache). Not bundled into CRA main chunk.
 *
 * Default: multilingual Whisper base using browser-compatible ONNX exports.
 * Spike can switch to small via createSpeechToTextService({ modelId: MODEL_IDS.small }).
 */

export const MODEL_IDS = Object.freeze({
  /** Multilingual base — recommended MVP */
  base: "onnx-community/whisper-base",
  /** Larger multilingual — use if TT terms fail on base */
  small: "onnx-community/whisper-small",
  /** Tiny multilingual — low-memory fallback */
  tiny: "onnx-community/whisper-tiny",
});

export const DEFAULT_MODEL_ID = MODEL_IDS.base;

/** WASM + q8 is the transformers.js browser default; fp32 OOMs after a few loads. */
export const WHISPER_DEVICE = "wasm";
export const WHISPER_DTYPE = "q8";

// Bump cache namespace when switching model families / runtime settings so
// browsers don't reuse older incompatible Whisper artifacts.
const CACHE_NAME = "smarttt-whisper-models-v3";

/**
 * @param {string} modelId
 * @returns {Promise<boolean>}
 */
export async function isModelCached(modelId) {
  if (typeof caches === "undefined") return false;
  try {
    const cache = await caches.open(CACHE_NAME);
    // transformers.js uses its own cache keys; we also track a marker.
    const marker = await cache.match(markerUrl(modelId));
    return !!marker;
  } catch {
    return false;
  }
}

/**
 * @param {string} modelId
 */
export async function markModelCached(modelId) {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(
      markerUrl(modelId),
      new Response(JSON.stringify({ modelId, cachedAt: Date.now() }), {
        headers: { "Content-Type": "application/json" },
      })
    );
  } catch {
    /* ignore */
  }
}

function markerUrl(modelId) {
  return `/voice-models/marker/${encodeURIComponent(modelId)}`;
}

/**
 * Approximate download progress from transformers.js progress callbacks.
 * @param {{ status?: string, progress?: number, file?: string, loaded?: number, total?: number }} ev
 * @returns {{ percent: number, file?: string, status: string }}
 */
export function normalizeProgressEvent(ev = {}) {
  const status = ev.status || "progress";
  let percent = 0;
  if (typeof ev.progress === "number") {
    percent = Math.round(Math.min(100, Math.max(0, ev.progress * (ev.progress <= 1 ? 100 : 1))));
    if (ev.progress > 1 && ev.progress <= 100) percent = Math.round(ev.progress);
  } else if (ev.loaded != null && ev.total) {
    percent = Math.round((ev.loaded / ev.total) * 100);
  }
  return { percent, file: ev.file, status };
}

export class ModelManager {
  constructor(modelId = DEFAULT_MODEL_ID) {
    this.modelId = modelId;
  }

  async isCached() {
    return isModelCached(this.modelId);
  }

  async markCached() {
    return markModelCached(this.modelId);
  }
}
